import _ from 'lodash';
import {
    PAYMENT_MODE,
    PAYMENT_MODE_OPTIONS,
} from '../invoicing/InvoicingConstants';
import { CURRENCY_OPTIONS } from './Constants';

// While adding a new country, add the following things here:
//   1. Country Constant
//   2. Country full name
//   3. Country to region mapping
//   4. Country to zip code regex mapping
//   5. Country to phone number regex mapping
//   6. Country specific constants
//        a. Common constants [used across multiple workflows]
//              i. Address field Labels/Placeholders
//              ii. Zip Code Regex
//              iii. Disclaimer Checks [Only if required, else do not add it]
//        b. Onboarding related constants
//              i. Currency options
//        c. Invoicing related constants
//              i. Tax Document i.e. W9 Form [Only if required, else do not add it]
//              ii. Bank Details (Bank routing number label/length)
//              iii. Wire Transfer Details

export const COUNTRY = {
    UK: 'UK',
    FRANCE: 'France',
    SPAIN: 'Spain',
    GERMANY: 'Germany',
    SWITZERLAND: 'Switzerland',
    BELGIUM: 'Belgium',
    US: 'US',
    CANADA: 'Canada',
};

// TODO: Actual country name to be replaced with the full name - https://desflow.hyd.deshaw.com/Q/Prop/334141
// TODO: Uncomment Switzerland once we start supporting it. Currently, it is commented to avoid viewing their Privacy notice from the main page.
const countryFullName = {
    [COUNTRY.UK]: 'United Kingdom',
    [COUNTRY.FRANCE]: 'France',
    [COUNTRY.SPAIN]: 'Spain',
    [COUNTRY.GERMANY]: 'Germany',
    // [COUNTRY.SWITZERLAND]: 'Switzerland',
    [COUNTRY.BELGIUM]: 'Belgium',
    [COUNTRY.US]: 'United States',
    [COUNTRY.CANADA]: 'Canada',
};

export const REGION = {
    AMERICA: 'America',
    EUROPE: 'Europe',
};

const COUNTRY_TO_REGION_MAPPING = {
    [COUNTRY.UK]: REGION.EUROPE,
    [COUNTRY.FRANCE]: REGION.EUROPE,
    [COUNTRY.SPAIN]: REGION.EUROPE,
    [COUNTRY.GERMANY]: REGION.EUROPE,
    [COUNTRY.SWITZERLAND]: REGION.EUROPE,
    [COUNTRY.BELGIUM]: REGION.EUROPE,
    [COUNTRY.US]: REGION.AMERICA,
    [COUNTRY.CANADA]: REGION.AMERICA,
};

export function getCountryFullName(country) {
    return countryFullName[country];
}

export function getCountryConstant(countryValue) {
    return _.findKey(countryFullName, (val) => _.isEqual(val, countryValue));
}

export function getCountriesByRegion(region) {
    return _.filter(COUNTRY, (country) =>
        _.isEqual(COUNTRY_TO_REGION_MAPPING[country], region)
    );
}

const countrySpecificZipCodeRegex = {
    [COUNTRY.UK]:
        /(^(([a-z]{1,2}\d[\da-z]?|ascn|sthl|tdcu|bbnd|[bfs]iqq|pcrn|tkca) ?\d[a-z]{2}|bfpo ?\d{1,4}|(ky\d|msr|vg|ai)[ -]?\d{4}|[a-z]{2} ?\d{2}|ge ?cx|gir ?0a{2}|san ?ta1)$)/i,
    [COUNTRY.FRANCE]: /^\d{5}$/,
    [COUNTRY.SPAIN]: /^\d{5}$/,
    [COUNTRY.GERMANY]: /^\d{5}$/,
    [COUNTRY.SWITZERLAND]: /^\d{4}$/,
    [COUNTRY.BELGIUM]: /^\d{4}$/,
    [COUNTRY.US]: /(^\d{5}$)|(^\d{5}-\d{4}$)/,
    [COUNTRY.CANADA]:
        /^[a-ceghj-npr-tvxy]\d[a-ceghj-npr-tv-z][ -]?\d[a-ceghj-npr-tv-z]\d$/i,
};

const countrySpecificPhoneNumberRegex = {
    [COUNTRY.UK]: /^(?=.{1,32}$)\d+(\.\d{1,10})?$/,
    [COUNTRY.FRANCE]: /^(?=.{1,32}$)\d+(\.\d{1,10})?$/,
    [COUNTRY.SPAIN]: /^(?=.{1,32}$)\d+(\.\d{1,10})?$/,
    [COUNTRY.GERMANY]: /^(?=.{1,32}$)\d+(\.\d{1,10})?$/,
    [COUNTRY.SWITZERLAND]: /^(?=.{1,32}$)\d+(\.\d{1,10})?$/,
    [COUNTRY.BELGIUM]: /^(?=.{1,32}$)\d+(\.\d{1,10})?$/,
    [COUNTRY.US]:
        /^((\+?1[ .-]?)?(\(?\d{3}\)?)[ .-]?\d{3}[ .-]?\d{4}|(?=.{1,32}$)\d+(\.\d{1,10})?)$/,
    [COUNTRY.CANADA]:
        /^((\+?1[ .-]?)?(\(?\d{3}\)?)[ .-]?\d{3}[ .-]?\d{4}|(?=.{1,32}$)\d+(\.\d{1,10})?)$/,
};

// Stores country specific constants
const countrySpecificConstants = {
    [COUNTRY.UK]: {
        common: {
            address: {
                labels: {
                    name: countryFullName[COUNTRY.UK],
                    city: 'City',
                    state: 'State / Territory / Constituent Country',
                    zipCode: 'Postal Code',
                },
                placeholders: {
                    city: 'e.g. London',
                    state: 'e.g. England / Scotland',
                    zipCode: 'e.g. XXX XXX',
                },
                regex: {
                    zipCode: countrySpecificZipCodeRegex[COUNTRY.UK],
                },
            },
        },
        onboarding: {
            currencyOptions: [CURRENCY_OPTIONS.EUR, CURRENCY_OPTIONS.GBP],
            contact: {
                regex: {
                    phoneNumber: countrySpecificPhoneNumberRegex[COUNTRY.UK],
                },
            },
            terms: {
                monitorAndRecordConsent: true,
            },
        },
        invoicing: {
            paymentModeOptions: [
                PAYMENT_MODE_OPTIONS[PAYMENT_MODE.BANK_PAYMENT],
            ],
            bankDetails: {
                bankRoutingNumber: {
                    label: 'Sort Code',
                    allowedLength: 6,
                },
            },
        },
    },
    [COUNTRY.FRANCE]: {
        common: {
            address: {
                labels: {
                    name: countryFullName[COUNTRY.FRANCE],
                    city: 'City',
                    state: 'Region',
                    zipCode: 'Postal Code',
                },
                placeholders: {
                    city: 'e.g. Paris',
                    state: 'e.g. Île-de-France',
                    zipCode: 'e.g. XXXXX',
                },
                regex: {
                    zipCode: countrySpecificZipCodeRegex[COUNTRY.FRANCE],
                },
            },
        },
        onboarding: {
            currencyOptions: [CURRENCY_OPTIONS.EUR],
            contact: {
                regex: {
                    phoneNumber:
                        countrySpecificPhoneNumberRegex[COUNTRY.FRANCE],
                },
            },
            terms: {
                monitorAndRecordConsent: true,
            },
        },
        invoicing: {
            paymentModeOptions: [
                PAYMENT_MODE_OPTIONS[PAYMENT_MODE.WIRE_TRANSFER],
            ],
            wireTransferDetails: {},
        },
    },
    [COUNTRY.SPAIN]: {
        common: {
            address: {
                labels: {
                    name: countryFullName[COUNTRY.SPAIN],
                    city: 'City',
                    state: 'Province',
                    zipCode: 'Postal Code',
                },
                placeholders: {
                    city: 'e.g. Madrid',
                    state: 'e.g. Madrid',
                    zipCode: 'e.g. XXXXX',
                },
                regex: {
                    zipCode: countrySpecificZipCodeRegex[COUNTRY.SPAIN],
                },
            },
        },
        onboarding: {
            currencyOptions: [CURRENCY_OPTIONS.EUR],
            contact: {
                regex: {
                    phoneNumber: countrySpecificPhoneNumberRegex[COUNTRY.SPAIN],
                },
            },
            terms: {
                monitorAndRecordConsent: true,
            },
        },
        invoicing: {
            paymentModeOptions: [
                PAYMENT_MODE_OPTIONS[PAYMENT_MODE.WIRE_TRANSFER],
            ],
            wireTransferDetails: {},
        },
    },
    [COUNTRY.GERMANY]: {
        common: {
            address: {
                labels: {
                    name: countryFullName[COUNTRY.GERMANY],
                    city: 'City',
                    state: 'State',
                    zipCode: 'Postal Code',
                },
                placeholders: {
                    city: 'e.g. Frankfurt',
                    state: 'e.g. Hesse',
                    zipCode: 'e.g. XXXXX',
                },
                regex: {
                    zipCode: countrySpecificZipCodeRegex[COUNTRY.GERMANY],
                },
            },
        },
        onboarding: {
            currencyOptions: [CURRENCY_OPTIONS.EUR],
            contact: {
                regex: {
                    phoneNumber:
                        countrySpecificPhoneNumberRegex[COUNTRY.GERMANY],
                },
            },
            terms: {
                monitorAndRecordConsent: true,
            },
        },
        invoicing: {
            paymentModeOptions: [
                PAYMENT_MODE_OPTIONS[PAYMENT_MODE.WIRE_TRANSFER],
            ],
            wireTransferDetails: {},
        },
    },
    [COUNTRY.SWITZERLAND]: {
        common: {
            address: {
                labels: {
                    name: countryFullName[COUNTRY.SWITZERLAND],
                    city: 'City',
                    state: 'Canton',
                    zipCode: 'Postal Code',
                },
                placeholders: {
                    city: 'e.g. Zürich',
                    state: 'e.g. Canton of Zürich',
                    zipCode: 'e.g. XXXX',
                },
                regex: {
                    zipCode: countrySpecificZipCodeRegex[COUNTRY.SWITZERLAND],
                },
            },
        },
        onboarding: {
            currencyOptions: [CURRENCY_OPTIONS.EUR, CURRENCY_OPTIONS.CHF],
            contact: {
                regex: {
                    phoneNumber:
                        countrySpecificPhoneNumberRegex[COUNTRY.SWITZERLAND],
                },
            },
            terms: {
                monitorAndRecordConsent: true,
            },
        },
        invoicing: {
            paymentModeOptions: [
                PAYMENT_MODE_OPTIONS[PAYMENT_MODE.WIRE_TRANSFER],
            ],
            wireTransferDetails: {},
        },
    },
    [COUNTRY.BELGIUM]: {
        common: {
            address: {
                labels: {
                    name: countryFullName[COUNTRY.BELGIUM],
                    city: 'City',
                    state: 'Province',
                    zipCode: 'Postal Code',
                },
                placeholders: {
                    city: 'e.g. Brussels',
                    state: 'e.g. Brussels-Capital Region',
                    zipCode: 'e.g. XXXX',
                },
                regex: {
                    zipCode: countrySpecificZipCodeRegex[COUNTRY.BELGIUM],
                },
            },
        },
        onboarding: {
            currencyOptions: [CURRENCY_OPTIONS.EUR],
            contact: {
                regex: {
                    phoneNumber:
                        countrySpecificPhoneNumberRegex[COUNTRY.BELGIUM],
                },
            },
            terms: {
                monitorAndRecordConsent: true,
            },
        },
        invoicing: {
            paymentModeOptions: [
                PAYMENT_MODE_OPTIONS[PAYMENT_MODE.WIRE_TRANSFER],
            ],
            wireTransferDetails: {},
        },
    },
    [COUNTRY.US]: {
        common: {
            address: {
                labels: {
                    name: countryFullName[COUNTRY.US],
                    city: 'City',
                    state: 'State / Territory',
                    zipCode: 'Zip Code',
                },
                placeholders: {
                    city: 'e.g. Austin',
                    state: 'e.g. Texas',
                    zipCode: 'e.g. XXXXX',
                },
                regex: {
                    zipCode: countrySpecificZipCodeRegex[COUNTRY.US],
                },
            },
        },
        onboarding: {
            currencyOptions: [CURRENCY_OPTIONS.USD],
            contact: {
                placeholders: {
                    phoneNumber: 'e.g. (XXX) XXX-XXXX',
                },
                regex: {
                    phoneNumber: countrySpecificPhoneNumberRegex[COUNTRY.US],
                },
            },
            terms: {
                monitorAndRecordConsent: false,
            },
        },
        invoicing: {
            taxDocument: {
                label: 'W9 Form',
                mandatory: () => true,
            },
            paymentModeOptions: [
                PAYMENT_MODE_OPTIONS[PAYMENT_MODE.BANK_PAYMENT],
                PAYMENT_MODE_OPTIONS[PAYMENT_MODE.MAIL_CHEQUE],
            ],
            bankDetails: {
                bankRoutingNumber: {
                    label: 'ACH Routing Number',
                    allowedLength: 9,
                },
            },
        },
    },
    [COUNTRY.CANADA]: {
        common: {
            address: {
                labels: {
                    name: countryFullName[COUNTRY.CANADA],
                    city: 'City',
                    state: 'Province / Territory',
                    zipCode: 'Postal Code',
                },
                placeholders: {
                    city: 'e.g. Toronto',
                    state: 'e.g. Ontario',
                    zipCode: 'e.g. A1A 1A1',
                },
                regex: {
                    zipCode: countrySpecificZipCodeRegex[COUNTRY.CANADA],
                },
            },
        },
        onboarding: {
            currencyOptions: [CURRENCY_OPTIONS.CAD, CURRENCY_OPTIONS.USD],
            contact: {
                placeholders: {
                    phoneNumber: 'e.g. (XXX) XXX-XXXX',
                },
                regex: {
                    phoneNumber:
                        countrySpecificPhoneNumberRegex[COUNTRY.CANADA],
                },
            },
            terms: {
                monitorAndRecordConsent: false,
            },
            disclaimerChecks: [
                {
                    name: 'nonQuebecResidentCheck',
                    text: `<div class="flex flex-col gap-2"><p>Please note that our ICP Onboarding form cannot support French-language documents, which documents Quebec law requires in order for us to engage Quebec-based experts.</p><p>By proceeding further, you confirm that <b>you are not based in the province of Quebec</b>.</p></div>`,
                },
            ],
        },
        invoicing: {
            taxDocument: {
                label: 'W-8BEN Form',
                mandatory: (invoiceValues) =>
                    _.isEqual(
                        invoiceValues?.consultantInvoiceAdditionalChecks
                            ?.wasInUSDuringConsultation,
                        'Yes'
                    ),
            },
            additionalChecks: {
                wasInUSDuringConsultation: {
                    text: 'Were you physically present in the United States during the consultation?',
                },
            },
            paymentModeOptions: [
                PAYMENT_MODE_OPTIONS[PAYMENT_MODE.WIRE_TRANSFER],
            ],
            wireTransferDetails: {},
        },
    },
};

// Individual function to access particular constant could be added here
export function getAddressFieldLabelsByCountry(country) {
    return countrySpecificConstants[country]?.common?.address?.labels;
}

export function getAddressFieldPlaceholdersByCountry(country) {
    return countrySpecificConstants[country]?.common?.address?.placeholders;
}

export function getContactFieldPlaceholdersByCountry(country) {
    return countrySpecificConstants[country]?.onboarding?.contact?.placeholders;
}

export function getZipCodeRegexByCountry(country) {
    return countrySpecificConstants[country]?.common?.address?.regex?.zipCode;
}

export function getPhoneNumberRegexByCountry(country) {
    return countrySpecificConstants[country]?.onboarding?.contact?.regex
        ?.phoneNumber;
}

export function getCurrencyOptionsByCountry(country) {
    return countrySpecificConstants[country]?.onboarding?.currencyOptions;
}

export function isMonitorAndRecordConsentRequired(country) {
    return countrySpecificConstants[country]?.onboarding?.terms
        ?.monitorAndRecordConsent;
}

// to check if tax document is supported for a country.
export function isTaxDocumentSupportedForCountry(country) {
    return _.has(countrySpecificConstants[country]?.invoicing, 'taxDocument');
}

export function getTaxDocumentLabelByCountry(country) {
    return countrySpecificConstants[country]?.invoicing?.taxDocument?.label;
}

// to check if tax document must be mandated for a country.
export function isTaxDocumentMandatoryForCountry(country, formValues) {
    // if tax document is not supported for the country, no point of mandatory concept.
    if (!isTaxDocumentSupportedForCountry(country)) {
        return false;
    }

    const mandatoryConfig =
        countrySpecificConstants[country]?.invoicing?.taxDocument?.mandatory;
    return mandatoryConfig(formValues);
}

export function getPaymentModeOptionsByCountry(country) {
    return countrySpecificConstants[country]?.invoicing?.paymentModeOptions;
}

export function isPaymentModeApplicableForCountry(country, paymentMode) {
    const paymentModeOptions = getPaymentModeOptionsByCountry(country);
    return _.some(paymentModeOptions, { key: paymentMode });
}

export function getBankRoutingNumberLabelByCountry(country) {
    return countrySpecificConstants[country]?.invoicing?.bankDetails
        ?.bankRoutingNumber?.label;
}

export function getBankRoutingNumberAllowedLengthByCountry(country) {
    return countrySpecificConstants[country]?.invoicing?.bankDetails
        ?.bankRoutingNumber?.allowedLength;
}

export function getInvoicingAdditionalChecksByCountry(country) {
    return countrySpecificConstants[country]?.invoicing?.additionalChecks;
}

export function getDisclaimerChecksByCountry(country) {
    return (
        countrySpecificConstants[country]?.onboarding?.disclaimerChecks || []
    );
}

export const CountryArrayToOptions = (countries) =>
    _.map(countries, (country) => ({
        label: getCountryFullName(country),
        key: getCountryFullName(country),
        value: getCountryFullName(country),
    }));
